import React, { useState } from 'react'
import { Alert, Box, Snackbar } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'

import { DetectIncomeDialog } from '@/components/Income/DetectIncome/DetectIncomeDialog'
import {
  detectionLoaderMs,
  useDetectionInProgress,
  useDetectionProgressStore,
} from '@/components/Income/DetectIncome/detectionProgress'
import { useIncomeDetection } from '@/components/Income/DetectIncome/useIncomeDetection'
import { ManualPayCycle } from '@/components/Income/ManualPayCycle'
import { PrimaryIncome } from '@/components/Income/PrimaryIncome'
import { RecurringIncome } from '@/components/Income/RecurringIncome'
import { incomeDetailsQueryKey } from '@/components/Income/useIncomeDetails'
import { useAppStore } from '@/store'
import { GENERIC_ERROR_MESSAGE } from '@/utils/constants'

const errorMessage = (error: unknown) =>
  error instanceof Error && error.message ? error.message : GENERIC_ERROR_MESSAGE

export const Income: React.FC = () => {
  // Detection is owned here because two sections open it: the primary income header, and the
  // manual pay cycle review as the step after an approval.
  const { detect, isDetecting } = useIncomeDetection()
  // The customer in this tab's header, not the IndexedDB record every tab shares.
  const { user } = useAppStore()
  // Captured when the dialog opens, so the detection runs for the customer the agent was looking
  // at when they asked for it. Doubles as the dialog's open state.
  const [detectUserId, setDetectUserId] = useState<string | null>(null)
  const startDetectionProgress = useDetectionProgressStore(state => state.start)
  const detectionInProgress = useDetectionInProgress(user?.id)
  const [detectError, setDetectError] = useState<string>()
  const [toast, setToast] = useState<string>()

  const detectionBusy = isDetecting || detectionInProgress

  const queryClient = useQueryClient()

  const openDetect = () => {
    if (!user?.id || detectionBusy) return
    setDetectError(undefined)
    setDetectUserId(user.id)
  }

  const confirmDetect = async () => {
    if (!detectUserId) return
    setDetectError(undefined)
    try {
      await detect(detectUserId)
      startDetectionProgress(detectUserId)
      // Read the income again as the loader ends, the first point results are expected
      // (VITE_INCOME_DETECTION_LOADER_SECONDS); useIncomeDetails then keeps polling for two minutes.
      // Keyed to the customer detection ran for, and deliberately not cleared on unmount: if the
      // agent has moved on, that customer's cached income is marked stale and refetched when next
      // shown.
      const userId = detectUserId
      setTimeout(
        () => queryClient.invalidateQueries({ queryKey: incomeDetailsQueryKey(userId) }),
        detectionLoaderMs()
      )
      setDetectUserId(null)
      setToast('Income detection requested.')
    } catch (e) {
      setDetectError(errorMessage(e))
    }
  }

  return (
    <>
      <PrimaryIncome
        onDetect={openDetect}
        isDetecting={isDetecting}
        detectionInProgress={detectionInProgress}
      />

      <Box mt={4}>
        <RecurringIncome />
      </Box>

      <Box mt={4}>
        <ManualPayCycle onDetectIncome={openDetect} detectionBusy={detectionBusy} />
      </Box>

      <DetectIncomeDialog
        open={detectUserId !== null}
        isDetecting={isDetecting}
        error={detectError}
        onCancel={() => setDetectUserId(null)}
        onConfirm={confirmDetect}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(undefined)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setToast(undefined)} sx={{ width: '100%' }}>
          {toast}
        </Alert>
      </Snackbar>
    </>
  )
}
